import React, { createContext, useContext, useEffect, useState } from 'react';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notificationsEnabled: boolean;
  enableNotifications: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType>({
  notificationsEnabled: false,
  enableNotifications: async () => false
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Check if notifications are already enabled
    const checkNotificationStatus = async () => {
      try {
        if (!('Notification' in window)) return;
        
        const permission = await Notification.permission;
        setNotificationsEnabled(permission === 'granted');
      } catch (error) {
        console.error('Error checking notification status:', error);
      }
    };

    checkNotificationStatus();
  }, [user]);

  useEffect(() => {
    if (!user || !notificationsEnabled) return;

    // Handle foreground messages
    const messaging = getMessaging();
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Received foreground message:', payload);
      
      // Show custom notification
      if (Notification.permission === 'granted') {
        const { title, body } = payload.notification;
        new Notification(title, {
          body,
          icon: '/icon-192x192.png',
          badge: '/badge-96x96.png'
        });
      }
    });

    return () => unsubscribe();
  }, [user, notificationsEnabled]);

  const enableNotifications = async () => {
    if (!user || !('Notification' in window)) return false;

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const messaging = getMessaging();
        const token = await getToken(messaging, {
          vapidKey: 'BAdjapIYdcIaRRMxzyCmBTMNT3wZaccKLYjjJ6d54QsjnsQmDjkwyuxC4LUBgwVH7j2c2lJRflbrrJeJvS3__7k'
        });

        // Save token to user's document
        await setDoc(doc(db, 'users', user.uid), {
          fcmToken: token,
          notificationsEnabled: true
        }, { merge: true });

        setNotificationsEnabled(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error enabling notifications:', error);
      return false;
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      notificationsEnabled,
      enableNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);